import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function getQuery() {
  const params = new URLSearchParams(window.location.search);
  const themeRaw = (params.get("theme") || "dark").toLowerCase();
  return {
    tenantId: params.get("tenantId") || "",
    lang: params.get("lang") || "auto",
    apiBase: (params.get("apiBase") || "").replace(/\/+$/, ""),
    theme: themeRaw === "light" ? "light" : "dark",
    primaryColor: params.get("primaryColor") || "#0ea5e9",
    title: params.get("title") || "Support",
    subtitle: params.get("subtitle") || "",
  };
}

function makeSessionId() {
  const rand = Math.random().toString(16).slice(2);
  const ts = Date.now().toString(16);
  return `sess_${ts}_${rand}`.slice(0, 64);
}

function formatTime(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

async function readBodySafe(res) {
  try {
    const text = await res.text();
    return text || "";
  } catch {
    return "";
  }
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isNearBottom(el, thresholdPx) {
  if (!el) return true;
  const diff = el.scrollHeight - el.scrollTop - el.clientHeight;
  return diff < thresholdPx;
}

function normalizePhone(raw) {
  const s = String(raw || "").trim();
  const digits = s.replace(/[^\d]/g, "");
  if (digits.length < 8) return "";
  return digits;
}

export default function Embed() {
  const q = useMemo(() => getQuery(), []);
  const tenantId = q.tenantId;
  const lang = q.lang;
  const apiBase = q.apiBase;
  const theme = q.theme;
  const primaryColor = q.primaryColor;
  const title = q.title;
  const subtitle = q.subtitle;

  const base = apiBase || "http://localhost:3000";

  const tokens = useMemo(() => {
    const isLight = theme === "light";
    return {
      isLight,
      bg: isLight ? "#ffffff" : "#0b1220",
      text: isLight ? "#0b1220" : "#ffffff",
      subText: isLight ? "rgba(11,18,32,.66)" : "rgba(255,255,255,.70)",
      border: isLight ? "rgba(11,18,32,.10)" : "rgba(255,255,255,.10)",
      soft: isLight ? "rgba(11,18,32,.05)" : "rgba(255,255,255,.06)",
      soft2: isLight ? "rgba(11,18,32,.08)" : "rgba(255,255,255,.10)",
      agentBubble: isLight ? "rgba(11,18,32,.06)" : "rgba(255,255,255,.10)",
      visitorBubble: primaryColor || "#0ea5e9",
      visitorText: "#ffffff",
      inputBg: isLight ? "rgba(11,18,32,.05)" : "rgba(255,255,255,.06)",
      inputBorder: isLight ? "rgba(11,18,32,.14)" : "rgba(255,255,255,.14)",
      ring: isLight ? "rgba(14,165,233,.35)" : "rgba(14,165,233,.45)",
      dangerBg: isLight ? "rgba(239,68,68,.08)" : "rgba(239,68,68,.10)",
      dangerBorder: isLight ? "rgba(239,68,68,.28)" : "rgba(239,68,68,.35)",
      dangerText: isLight ? "rgba(11,18,32,.92)" : "rgba(255,255,255,.92)",
    };
  }, [theme, primaryColor]);

  const [status, setStatus] = useState("Connecting...");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [debugError, setDebugError] = useState("");
  const [debugOpen, setDebugOpen] = useState(false);

  // Visitor identity (Name + Phone)
  const [visitorReady, setVisitorReady] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [visitorErr, setVisitorErr] = useState("");

  const listRef = useRef(null);
  const inputRef = useRef(null);

  const storageKeySession = useMemo(() => {
    return tenantId ? `air_widget_session_${tenantId}` : "air_widget_session";
  }, [tenantId]);

  const storageKeyVisitor = useMemo(() => {
    return tenantId ? `air_widget_visitor_${tenantId}` : "air_widget_visitor";
  }, [tenantId]);

  const [sessionId, setSessionId] = useState("");

  // Scroll management (only autoscroll when user is near bottom)
  const shouldStickRef = useRef(true);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (shouldStickRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, sending]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    shouldStickRef.current = isNearBottom(el, 180);
  }, []);

  // Load saved visitor profile (name + phone)
  useEffect(() => {
    if (!tenantId) {
      setStatus("Missing tenantId.");
      setVisitorReady(false);
      return;
    }

    try {
      const raw = localStorage.getItem(storageKeyVisitor) || "";
      const obj = raw ? safeJsonParse(raw) : null;
      const name = String(obj?.name || "").trim();
      const phone = String(obj?.phone || "").trim();
      if (name && phone) {
        setVisitorName(name);
        setVisitorPhone(phone);
        setVisitorReady(true);
      } else {
        setVisitorReady(false);
      }
    } catch {
      setVisitorReady(false);
    }

    setStatus("Ready");
  }, [tenantId, storageKeyVisitor]);

  // Init session ONLY after visitorReady
  useEffect(() => {
    if (!tenantId) return;
    if (!visitorReady) return;

    let sid = "";
    try {
      sid = localStorage.getItem(storageKeySession) || "";
    } catch {}

    if (!sid) {
      sid = makeSessionId();
      try {
        localStorage.setItem(storageKeySession, sid);
      } catch {}
    }

    setSessionId(sid);
  }, [tenantId, visitorReady, storageKeySession]);

  // Focus when widget opens
  useEffect(() => {
    function onMessage(ev) {
      const data = ev?.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "AIR_CHAT_OPEN") {
        if (!visitorReady) return;
        setTimeout(() => inputRef.current?.focus?.(), 80);
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [visitorReady]);

  const loadHistory = useCallback(
    async (sid) => {
      if (!tenantId || !sid) return;

      setStatus("Loading...");
      setDebugError("");
      setDebugOpen(false);

      try {
        const phoneNorm = normalizePhone(visitorPhone);

        const url =
          `${base}/api/widget/chat/history` +
          `?tenantId=${encodeURIComponent(tenantId)}` +
          `&sessionId=${encodeURIComponent(sid)}` +
          (phoneNorm ? `&phone=${encodeURIComponent(phoneNorm)}` : "") +
          `&limit=200`;

        const r = await fetch(url, { method: "GET" });
        const bodyText = await readBodySafe(r);
        const j = safeJsonParse(bodyText);

        if (!r.ok || !j?.ok) {
          const msg = `HISTORY ERROR: HTTP ${r.status} ${r.statusText}\n${bodyText}`;
          console.error(msg);
          setDebugError(msg);
          setDebugOpen(true);
          setStatus("Failed to load.");
          return;
        }

        const items = Array.isArray(j.items) ? j.items : [];
        setMessages(
          items.map((m) => ({
            id: m._id || `${m.role}_${m.createdAt}`,
            role: m.role,
            text: m.text,
            ts: m.createdAt || new Date().toISOString(),
          }))
        );

        // after initial load, stick to bottom
        shouldStickRef.current = true;
        setStatus("Ready");
      } catch (e) {
        const msg = `HISTORY EXCEPTION: ${e?.message || String(e)}`;
        console.error(msg, e);
        setDebugError(msg);
        setDebugOpen(true);
        setStatus("Failed to load.");
      }
    },
    [base, tenantId, visitorPhone]
  );

  useEffect(() => {
    if (!tenantId || !visitorReady || !sessionId) return;
    loadHistory(sessionId);
  }, [tenantId, visitorReady, sessionId, loadHistory]);

  const headerStatus = useMemo(() => {
    if (status === "Ready") return "Online";
    if (status === "Loading...") return "Loading";
    if (status === "Connecting...") return "Connecting";
    if (/missing/i.test(status)) return "Setup needed";
    if (/failed/i.test(status)) return "Issue";
    return status || "Status";
  }, [status]);

  const saveVisitor = useCallback(() => {
    const name = String(visitorName || "").trim();
    const phoneNorm = normalizePhone(visitorPhone);

    if (!name) {
      setVisitorErr("Please enter your name.");
      return false;
    }
    if (!phoneNorm) {
      setVisitorErr("Please enter a valid phone number.");
      return false;
    }

    setVisitorErr("");

    try {
      localStorage.setItem(storageKeyVisitor, JSON.stringify({ name, phone: phoneNorm }));
    } catch {}

    setVisitorName(name);
    setVisitorPhone(phoneNorm);
    setVisitorReady(true);

    // Focus input when ready
    setTimeout(() => inputRef.current?.focus?.(), 120);

    return true;
  }, [visitorName, visitorPhone, storageKeyVisitor]);

  const resetVisitor = useCallback(() => {
    try {
      localStorage.removeItem(storageKeyVisitor);
    } catch {}
    setVisitorReady(false);
    setVisitorErr("");
    setVisitorName("");
    setVisitorPhone("");
    setMessages([]);
    setSessionId("");
  }, [storageKeyVisitor]);

  const sendMessage = useCallback(async () => {
    const text = String(input || "").trim();
    if (!text || sending) return;
    if (!visitorReady) return;

    setInput("");
    setSending(true);
    setDebugError("");
    setDebugOpen(false);

    const optimistic = {
      id: `local_${Date.now()}`,
      role: "visitor",
      text,
      ts: new Date().toISOString(),
    };

    // If user is near bottom, keep it sticky
    const el = listRef.current;
    shouldStickRef.current = isNearBottom(el, 200);

    setMessages((prev) => [...prev, optimistic]);

    try {
      const payload = {
        tenantId,
        sessionId,
        text,
        meta: {
          referrer: document.referrer || "",
          lang,
          source: "web-widget",
          ua: navigator.userAgent || "",
          visitor: {
            name: String(visitorName || "").trim(),
            phone: String(visitorPhone || "").trim(),
          },
        },
      };

      const r = await fetch(`${base}/api/widget/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const bodyText = await readBodySafe(r);
      const j = safeJsonParse(bodyText);

      if (!r.ok || !j?.ok) {
        const msg = `SEND ERROR: HTTP ${r.status} ${r.statusText}\n${bodyText}`;
        console.error(msg);
        setDebugError(msg);
        setDebugOpen(true);

        setMessages((prev) => [
          ...prev,
          {
            id: `err_${Date.now()}`,
            role: "agent",
            text: "Sorry, something went wrong. Please try again.",
            ts: new Date().toISOString(),
          },
        ]);
        setSending(false);
        return;
      }

      const agentText = String(j?.agent?.text || "").trim();
      if (agentText) {
        setMessages((prev) => [
          ...prev,
          {
            id: j?.agent?._id || `agent_${Date.now()}`,
            role: "agent",
            text: agentText,
            ts: j?.agent?.createdAt || new Date().toISOString(),
          },
        ]);
      }

      window.parent?.postMessage({ type: "AIR_CHAT_BADGE", count: 0 }, "*");
      setSending(false);
    } catch (e) {
      const msg = `SEND EXCEPTION: ${e?.message || String(e)}`;
      console.error(msg, e);
      setDebugError(msg);
      setDebugOpen(true);

      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: "agent",
          text: "Sorry, something went wrong. Please try again.",
          ts: new Date().toISOString(),
        },
      ]);
      setSending(false);
    }
  }, [
    input,
    sending,
    tenantId,
    sessionId,
    lang,
    base,
    visitorReady,
    visitorName,
    visitorPhone,
  ]);

  const canSend = visitorReady && !sending && String(input || "").trim().length > 0;

  // ===== Visitor Gate UI =====
  if (!visitorReady) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          background: tokens.bg,
          color: tokens.text,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            height: 56,
            padding: "0 14px",
            borderBottom: `1px solid ${tokens.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: tokens.bg,
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                background: tokens.soft2,
                border: `1px solid ${tokens.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-hidden="true"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 5.5C4 4.12 5.12 3 6.5 3h11C19.88 3 21 4.12 21 5.5v7.5c0 1.38-1.12 2.5-2.5 2.5H10l-4.2 3.15c-.66.5-1.8.02-1.8-.86V15.5c0-.55.45-1 1-1h.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
              <div style={{ fontSize: 13, fontWeight: 850, letterSpacing: 0.2 }}>{title}</div>
              <div style={{ fontSize: 11, color: tokens.subText, marginTop: 3 }}>
                Please enter your details to start.
              </div>
            </div>
          </div>

          <button
            onClick={() => window.parent?.postMessage({ type: "AIR_CHAT_REQUEST_CLOSE" }, "*")}
            style={{
              border: `1px solid ${tokens.border}`,
              background: tokens.soft,
              color: tokens.text,
              width: 38,
              height: 38,
              borderRadius: 14,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close chat"
            title="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: 14, flex: 1, display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "100%",
              border: `1px solid ${tokens.border}`,
              background: tokens.soft,
              borderRadius: 18,
              padding: 14,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 10 }}>Start chat</div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 12, color: tokens.subText, fontWeight: 800 }}>Name</div>
                <input
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  style={{
                    height: 44,
                    borderRadius: 12,
                    border: `1px solid ${tokens.inputBorder}`,
                    background: tokens.inputBg,
                    color: tokens.text,
                    padding: "0 12px",
                    outline: "none",
                    fontSize: 13,
                  }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 12, color: tokens.subText, fontWeight: 800 }}>Phone</div>
                <input
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                  placeholder="Your phone number"
                  autoComplete="tel"
                  inputMode="tel"
                  style={{
                    height: 44,
                    borderRadius: 12,
                    border: `1px solid ${tokens.inputBorder}`,
                    background: tokens.inputBg,
                    color: tokens.text,
                    padding: "0 12px",
                    outline: "none",
                    fontSize: 13,
                  }}
                />
                <div style={{ fontSize: 11, color: tokens.subText }}>
                  We use this only to identify your conversation.
                </div>
              </div>

              {visitorErr ? (
                <div
                  style={{
                    padding: "10px 10px",
                    borderRadius: 14,
                    border: `1px solid ${tokens.dangerBorder}`,
                    background: tokens.dangerBg,
                    color: tokens.dangerText,
                    fontSize: 12,
                    lineHeight: 1.35,
                  }}
                >
                  {visitorErr}
                </div>
              ) : null}

              <button
                onClick={saveVisitor}
                style={{
                  height: 44,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: 0,
                  cursor: "pointer",
                  background: primaryColor,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 900,
                }}
              >
                Continue
              </button>

              <div style={{ fontSize: 11, color: tokens.subText, textAlign: "center" }}>
                Powered by AIR
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Chat UI =====
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        background: tokens.bg,
        color: tokens.text,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar (ONLY header in the whole widget) */}
      <div
        style={{
          height: 56,
          padding: "0 14px",
          borderBottom: `1px solid ${tokens.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: tokens.bg,
        }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 12,
              background: tokens.soft2,
              border: `1px solid ${tokens.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-hidden="true"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 5.5C4 4.12 5.12 3 6.5 3h11C19.88 3 21 4.12 21 5.5v7.5c0 1.38-1.12 2.5-2.5 2.5H10l-4.2 3.15c-.66.5-1.8.02-1.8-.86V15.5c0-.55.45-1 1-1h.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <div style={{ fontSize: 13, fontWeight: 850, letterSpacing: 0.2 }}>{title}</div>
            <div style={{ fontSize: 11, color: tokens.subText, marginTop: 3 }}>
              {subtitle
                ? subtitle
                : headerStatus === "Online"
                ? `Online • Chatting as ${visitorName} (${visitorPhone})`
                : headerStatus}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {debugError ? (
            <button
              onClick={() => setDebugOpen((v) => !v)}
              style={{
                border: `1px solid ${tokens.border}`,
                background: tokens.soft,
                color: tokens.text,
                padding: "7px 10px",
                borderRadius: 12,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 800,
              }}
              title="Toggle debug"
            >
              Debug
            </button>
          ) : null}

          <button
            onClick={resetVisitor}
            style={{
              border: `1px solid ${tokens.border}`,
              background: tokens.soft,
              color: tokens.text,
              padding: "7px 10px",
              borderRadius: 12,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 900,
            }}
            title="Change name/phone"
          >
            Edit
          </button>

          <button
            onClick={() => window.parent?.postMessage({ type: "AIR_CHAT_REQUEST_CLOSE" }, "*")}
            style={{
              border: `1px solid ${tokens.border}`,
              background: tokens.soft,
              color: tokens.text,
              width: 38,
              height: 38,
              borderRadius: 14,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close chat"
            title="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Debug panel (optional) */}
      {debugError && debugOpen ? (
        <div
          style={{
            margin: "10px 14px 0 14px",
            padding: "10px 10px",
            borderRadius: 14,
            border: `1px solid ${tokens.dangerBorder}`,
            background: tokens.dangerBg,
            color: tokens.dangerText,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            lineHeight: 1.35,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 6 }}>Debug details</div>
          {debugError}
        </div>
      ) : null}

      {/* Messages */}
      <div
        ref={listRef}
        onScroll={onScroll}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "14px 14px 10px 14px",
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              paddingTop: 10,
              textAlign: "center",
              color: tokens.subText,
              fontSize: 13,
            }}
          >
            Send a message to start the conversation.
          </div>
        ) : null}

        {messages.map((m) => {
          const isVisitor = m.role === "visitor";
          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: isVisitor ? "flex-end" : "flex-start",
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  maxWidth: "84%",
                  background: isVisitor ? tokens.visitorBubble : tokens.agentBubble,
                  color: isVisitor ? tokens.visitorText : tokens.text,
                  padding: "10px 12px",
                  borderRadius: 16,
                  fontSize: 13,
                  lineHeight: 1.38,
                  whiteSpace: "pre-wrap",
                  border: isVisitor ? "none" : `1px solid ${tokens.border}`,
                }}
              >
                <div>{m.text}</div>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.72 }}>
                  {formatTime(m.ts)}
                </div>
              </div>
            </div>
          );
        })}

        {sending ? (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
            <div
              style={{
                maxWidth: "84%",
                background: tokens.agentBubble,
                border: `1px solid ${tokens.border}`,
                padding: "10px 12px",
                borderRadius: 16,
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              Typing…
            </div>
          </div>
        ) : null}
      </div>

      {/* Composer */}
      <div
        style={{
          padding: 14,
          borderTop: `1px solid ${tokens.border}`,
          background: tokens.bg,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: 8,
            borderRadius: 16,
            border: `1px solid ${tokens.border}`,
            background: tokens.soft,
          }}
        >
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            placeholder="Type your message…"
            style={{
              flex: 1,
              height: 44,
              borderRadius: 12,
              border: `1px solid ${tokens.inputBorder}`,
              background: tokens.inputBg,
              color: tokens.text,
              padding: "0 12px",
              outline: "none",
              fontSize: 13,
            }}
          />

          <button
            onClick={sendMessage}
            disabled={!canSend}
            style={{
              height: 44,
              padding: "0 14px",
              borderRadius: 12,
              border: 0,
              cursor: canSend ? "pointer" : "default",
              background: canSend ? primaryColor : tokens.soft2,
              color: canSend ? "#fff" : tokens.subText,
              fontSize: 13,
              fontWeight: 900,
              minWidth: 86,
              opacity: canSend ? 1 : 0.85,
            }}
          >
            Send
          </button>
        </div>

        <div style={{ marginTop: 8, fontSize: 11, color: tokens.subText, textAlign: "center" }}>
          Powered by AIR
        </div>
      </div>
    </div>
  );
}
